import { useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import type { TaskCard as TaskCardData } from '../../services/api/board';
import { SlaTaskCard } from '../projects/SlaTaskCard';
import { SLA_TASK_TYPE_ID } from '../../constants/typeIds';
import { Avatar } from '../ui/Avatar';
import {
  getTierColor,
  chipTextColor,
  taskStalenessEmoji,
  DISABLED_TASK_STALENESS_CONFIG,
  type TaskStalenessConfig,
} from '../../utils/format';
import { MtPriorityBadge } from '../mytasks/MtPriorityBadge';
import { TaskLabelChips } from './TaskLabelChips';
import { TaskDueChip } from './TaskDueChip';
import { useBlockerHighlight } from './blockerHighlightContext';
import { useInternalChipVisibility } from './internalChipVisibility';
import {
  blockRefsTooltip,
  formatBlockRefCodes,
  type BlockDirection,
} from '../../utils/taskBlockers';
import type { TaskBlockRef } from '../../services/api/board';

export interface TaskCardProps {
  task: TaskCardData;
  audience: 'internal' | 'external';
  canDrag: boolean;
  /**
   * Phase 41 Plan 41-08 (Surface 5d-A) — parent project's `taskTypeId`.
   * When equal to `SLA_TASK_TYPE_ID` the card mounts the inline SLA
   * dual-bar timer (CONTEXT D-4).
   *
   * Per checker I-09 (verified 2026-05-11): `TaskWithStats`/`TaskCardData`
   * does NOT eagerly load the project, so the kanban surface (which DOES
   * load the project to render the board) passes this down explicitly.
   * Forbidden alternative: `task.project?.taskTypeId` access — that path
   * does not exist on the wire and would always read `undefined`.
   */
  projectTaskTypeId?: number | null;
  /**
   * Petra UAT 2026-05-19 — task_states.color hex for the column this card
   * belongs to. When provided the card renders a 4 px colored left border
   * so each task on the board visually matches its column header dot and
   * the row tint on /my-tasks. KanbanSwimlaneCell passes column.color down;
   * external/TaskCardDragOverlay can omit (falls back to neutral border).
   */
  accentColor?: string | null;
  /**
   * 260723-tst — whether the column this card sits in is a terminal state
   * (Done / Archived / Cancelled / ...). Suppresses the "getting old" 💩 age
   * emoji. KanbanSwimlaneCell passes `column.isTerminal`; omit it (drag
   * overlay, tests) to treat the task as in-progress.
   */
  isTerminal?: boolean;
  /**
   * 260913-jpy — per-project staleness indicator config. Omitted means the
   * caller never wired this prop; the card substitutes
   * `DISABLED_TASK_STALENESS_CONFIG` rather than the always-on global
   * ladder — a project surface that was never threaded shows no emoji
   * rather than silently reverting to the pre-change behavior.
   */
  stalenessConfig?: TaskStalenessConfig;
  /**
   * MKS-419 — set only on the merged parent + subprojects board, for a card
   * that belongs to a SUBPROJECT. Renders a small neutral chip with the
   * subproject's slug (full name as tooltip). Omitted: the card renders exactly
   * as before.
   */
  subprojectBadge?: { slug: string; name: string | null } | null;
  onClick: (task: TaskCardData) => void;
  onFlip: (task: TaskCardData) => void;
}

/**
 * Phase 24 Plan 24-04 — task card with conditional drag + indicator slot.
 *
 * Indicator slot (top-right) renders one of three states:
 *   - audience='external'           → null (no slot at all per TASK-03 / EXT-02)
 *   - canFlipVisibility=true        → eye-button toggle (D-04)
 *   - canFlipVisibility=false       → static green/amber chip (read-only)
 *
 * Drag listeners are spread ONLY when canDrag is true; external surface MUST
 * pass disabled=true via canDrag=false so dnd-kit never activates a drag.
 */
export function TaskCard({
  task,
  audience,
  canDrag,
  projectTaskTypeId,
  accentColor,
  isTerminal,
  stalenessConfig,
  subprojectBadge,
  onClick,
  onFlip,
}: TaskCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: String(task.id),
    disabled: !canDrag,
    data: { task },
  });

  // MKS-292 — the block relations are internal-only (the board never sends them
  // to an external actor either).
  const blockRelationsOn = audience === 'internal';
  const blockedBy = blockRelationsOn ? (task.blockedBy ?? []) : [];
  const blocks = blockRelationsOn ? (task.blocks ?? []) : [];
  const { isHighlighted } = useBlockerHighlight();
  // MKS-534 — on a project with no external collaborators the "Internal" chip
  // states the only thing a ticket there can be, so the board hides it. Read
  // unconditionally (stable hook order); inert without a provider.
  const { isInternalChipHidden } = useInternalChipVisibility();
  // Ring this card when it is a counterpart of whatever line is hovered right
  // now, anywhere on the board — including a counterpart hidden behind `+N`.
  const isBlockHighlighted = isHighlighted(task.id);

  const style = {
    // 260722-fpb — the DragOverlay is the moving ghost, so the source card must
    // NOT follow the pointer. dnd-kit's `transform` here includes scaleX/scaleY
    // relative to the hovered droppable (a tall empty cell → scaleY ~4), which
    // rendered a giant stretched clone of the card next to the overlay.
    opacity: isDragging ? 0.5 : 1,
    // Petra UAT 2026-05-19 — colored left border in the column color so
    // tasks on the board pick up the same hue as their column header dot
    // and the MyTaskRow tint. Falls back to ms-border (neutral) when no
    // accentColor is supplied (e.g. TaskCardDragOverlay).
    borderLeftColor: accentColor ?? undefined,
    borderLeftWidth: accentColor ? '4px' : undefined,
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(task);
  };

  const handleFlip = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFlip(task);
  };

  // MKS-198 — the card title is a real link to the permanent full-page URL, so
  // middle-click / Cmd+click / the browser's own "Open link in new tab" work on
  // a board card. Same URL the slide-over's "Copy link" writes for an internal
  // viewer: `/tasks/{NUMERIC id}` (codes are not DB-unique, ids always are).
  //
  // Internal audience only: `/tasks/*` is an internal route the external portal
  // must never surface — the same reason the portal callers never pass
  // `onOpenFullPage` to TaskDetailSlideOver.
  const titleIsLink = audience === 'internal';
  const taskUrl = `/tasks/${task.id}`;

  // A plain left click keeps today's behaviour: suppress the navigation and let
  // the event bubble to the card's own handleClick, so exactly one code path
  // opens the slide-over. A modified click is left to the browser (new tab /
  // new window) and stops there, so the panel does not open behind it. Middle
  // click dispatches `auxclick`, not `click`, so neither handler runs and the
  // browser opens the tab on its own.
  //
  // Drag is unaffected: the dnd-kit listeners stay on the card root, the anchor
  // adds no pointer handlers, and because every plain click is prevented, a
  // click synthesised at the end of a drag can never navigate.
  const handleTitleClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      e.stopPropagation();
      return;
    }
    e.preventDefault();
  };

  const dragProps = canDrag ? { ...attributes, ...listeners } : {};
  const externalTooltip = audience === 'external' ? { title: 'Read-only' } : {};

  // MKS-490 — the meta cluster (staleness emoji, avatar, External pill,
  // visibility chip, priority) renders in a footer row under the title, so the
  // title gets the full card width in the narrower fixed-width columns.
  const metaCluster = (
    <>
      <div className="flex items-center gap-1.5 shrink-0">
        {/* 260723 — internal-only staleness emoji (💩 >5d, 🪦 >15d with no
          update; in-progress only). Gated on audience so external viewers
          never see it. */}
        {audience === 'internal' &&
          (() => {
            // 260913-jpy — substitute the disabled default when this
            // surface was never wired to a real per-project config, so
            // an unwired call site shows nothing rather than falling
            // back to the always-on global ladder.
            const staleEmoji = taskStalenessEmoji(task.lastActivityAt, {
              isTerminal,
              config: stalenessConfig ?? DISABLED_TASK_STALENESS_CONFIG,
            });
            return staleEmoji ? (
              <span
                role="img"
                aria-label={staleEmoji.label}
                title={staleEmoji.label}
                className="text-sm leading-none shrink-0"
              >
                {staleEmoji.emoji}
              </span>
            ) : null;
          })()}
        {/* Petra UAT 2026-05-19 — assignee avatar (xs). Shown only when the
          task has an assignee; unassigned tasks render no chip so the
          "free market" visual cue stays distinct (D-03). */}
        {task.assigneeName && (
          <Avatar
            name={task.assigneeName}
            colorSeq={task.assigneeColorSeq}
            avatarUrl={task.assigneeAvatarUrl}
            size="xs"
          />
        )}
        {/* Phase 66 (EAP-07) — amber "External" pill for externally-assigned
          tasks. Gated on assigneeKind so internal/unassigned rendering is
          byte-for-byte unchanged. */}
        {task.assigneeName && task.assigneeKind === 'external_user' && (
          <span className="text-[9px] font-semibold px-1 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0">
            External
          </span>
        )}
        {renderIndicator(task, audience, handleFlip, isInternalChipHidden(task.projectId))}
      </div>
      {/* MKS-163 follow-up (supersedes D-2) — priority now renders on
        EVERY card, including 'normal', as a second row directly under
        the Internal/External visibility chip. 'normal' defaults when
        the wire value is missing (legacy fixtures / NOT NULL DEFAULT
        'normal' column). This is the ONLY place priority renders on the
        card — the header-row dot next to the code/tier was removed so
        priority appears in exactly one place. */}
      <MtPriorityBadge priority={task.priority ?? 'normal'} withLabel />
    </>
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...dragProps}
      {...externalTooltip}
      onClick={handleClick}
      // Phase 41 Plan 41-17 (Task 1) — v7 token recolor (D-Kanban-recolor):
      // bg-white → bg-ms-surface, border-gray-200 → border-ms-border.
      data-testid={`task-card-${task.id}`}
      // MKS-292 — `ring-inset` so the red ring does not shift the card or
      // overlap its neighbours in a tight column.
      className={`rounded-lg border bg-ms-surface p-3 shadow-sm hover:shadow-md transition-shadow border-ms-border ${
        canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
      } ${isBlockHighlighted ? 'ring-2 ring-inset ring-red-500' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {(task.tier || task.code || subprojectBadge) && (
            <div
              data-testid="task-card-header-row"
              className="flex items-center gap-1.5 mb-0.5"
            >
              {task.tier && (
                <span
                  title={task.tier.name}
                  data-testid={`task-card-tier-badge-${task.id}`}
                  style={{
                    backgroundColor: getTierColor(task.tier.orderIndex),
                    color: chipTextColor(getTierColor(task.tier.orderIndex)),
                  }}
                  className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-bold leading-none shrink-0 hover:ring-2 hover:ring-ms-border transition-shadow"
                >
                  P{task.tier.orderIndex}
                </span>
              )}
              {subprojectBadge && (
                <span
                  data-testid="task-card-subproject-badge"
                  title={subprojectBadge.name ?? subprojectBadge.slug}
                  aria-label={subprojectBadge.name ?? subprojectBadge.slug}
                  className="inline-flex items-center rounded border border-ms-border bg-ms-canvas px-1.5 py-0.5 text-[10px] font-semibold leading-none text-ms-text-secondary shrink-0"
                >
                  {subprojectBadge.slug}
                </span>
              )}
              {task.code && (
                <span className="text-[10px] font-mono font-semibold uppercase tracking-wide text-ms-text-secondary truncate">
                  {task.code}
                </span>
              )}
            </div>
          )}
          {/* Phase 41 Plan 41-17 — text-gray-900 → text-ms-text-strong. */}
          {/* MKS-133 — two-line clamp instead of single-line truncate: narrow
              columns left only a few characters visible. Full name stays in
              the title attr for hover. */}
          <h4 className="text-sm font-medium text-ms-text-strong line-clamp-2" title={task.name}>
            {titleIsLink ? (
              <a href={taskUrl} onClick={handleTitleClick} className="hover:underline">
                {task.name}
              </a>
            ) : (
              task.name
            )}
          </h4>
        </div>
      </div>
      <div
        data-testid="task-card-meta-footer"
        className="mt-1.5 flex flex-wrap items-center gap-1.5"
      >
        {metaCluster}
      </div>
      {/* Phase 41 Plan 41-08 (Surface 5d-A) — inline SLA timer dual bars
       * when the parent project is on the SLA Task template (CONTEXT D-4
       * 5d-A: inline only — uniform density across kanban + detail). The
       * SlaTaskCard wrapper itself further short-circuits when the timer
       * endpoint 403s/404s (T-41-22 graceful degradation).
       * Phase 41 Plan 41-17 — border-gray-100 → border-ms-border-subtle. */}
      {projectTaskTypeId === SLA_TASK_TYPE_ID && (
        <div className="mt-2 pt-2 border-t border-ms-border-subtle">
          <SlaTaskCard taskId={task.id} enabled={true} density="inline" />
        </div>
      )}
      {/* MKS-164 — label chip row, bottom of the card body (outside the
          right-side flex-col cluster, which is already two rows deep with
          the visibility chip and the priority badge). Renders nothing for
          a label-less task. */}
      {/* MKS-341 — the due-date chip, on its own row under the label chips and
          above the block lines. Renders nothing when the ticket has no due
          date, so a card without one is byte-for-byte what it was.

          `isTerminal` is the prop the card already receives for the staleness
          emoji; threading the SAME value here is what satisfies "terminal
          tickets are never marked overdue". A surface that omits the prop (the
          drag overlay, a test fixture) treats the card as in-progress, exactly
          as the emoji does. */}
      {task.dueDate ? (
        <div className="mt-2 flex">
          <TaskDueChip dueDate={task.dueDate} isTerminal={isTerminal} />
        </div>
      ) : null}
      <TaskLabelChips labels={task.labels} />
      {/* MKS-292 — the two block lines, at the very bottom of the card below
          the label chips. Each renders nothing when its direction is empty, so
          an unblocked card is byte-for-byte what it was. */}
      <BlockRelationLine refs={blockedBy} direction="blockedBy" />
      <BlockRelationLine refs={blocks} direction="blocks" />
    </div>
  );
}

/**
 * MKS-292 — one "Blocked by MKS-266, MKS-279" / "Blocks MKS-289 +2" line.
 *
 * Hovering it publishes the counterpart ids to the board-level highlight
 * context, which rings those cards in red — including the ones collapsed into
 * `+N`, and harmlessly none at all when a counterpart is not on this board. The
 * `title` always spells out every counterpart and why it is there.
 *
 * `focus`/`blur` mirror `mouseenter`/`mouseleave` so the highlight is reachable
 * without a pointer; the click is left to bubble so the whole card stays one
 * click target that opens the task.
 */
function BlockRelationLine({
  refs,
  direction,
}: {
  refs: TaskBlockRef[];
  direction: BlockDirection;
}) {
  const { highlight, clearHighlight } = useBlockerHighlight();
  // Until when a `mouseleave` is our own scroll moving this line out from
  // under a still pointer (counterpart in the same column), not the user
  // leaving — that one must not drop the highlight we just scrolled to.
  const autoScrollUntil = useRef(0);

  if (refs.length === 0) return null;

  const taskIds = refs.map((ref) => ref.taskId);
  const blockedByDirection = direction === 'blockedBy';

  // A counterpart scrolled out of its column is rung but invisible, so bring
  // the first one on this board into view. `nearest` is a no-op when it is
  // already visible and never scrolls further than needed.
  const revealCounterpart = () => {
    const target = taskIds
      .map((id) => document.querySelector<HTMLElement>(`[data-testid="task-card-${id}"]`))
      .find((el): el is HTMLElement => el !== null);
    if (!target || isFullyVisible(target)) return;
    autoScrollUntil.current = Date.now() + 1000;
    target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  };

  const handleEnter = () => {
    highlight(taskIds);
    revealCounterpart();
  };

  const handleLeave = () => {
    if (Date.now() < autoScrollUntil.current) return;
    clearHighlight();
  };
  return (
    <div
      data-testid={`task-card-${blockedByDirection ? 'blocked-by' : 'blocks'}`}
      title={blockRefsTooltip(refs, direction)}
      tabIndex={0}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={clearHighlight}
      // Red for "I am blocked", amber for "I block somebody else" — the second
      // is information, not a problem with this card. The hover RING is red in
      // both directions.
      className={`mt-2 flex items-center gap-1 text-[11px] font-medium cursor-help ${
        blockedByDirection ? 'text-red-600' : 'text-amber-600'
      }`}
    >
      <NoSymbolIcon className="h-3 w-3 shrink-0" aria-hidden />
      <span className="truncate">
        {blockedByDirection ? 'Blocked by' : 'Blocks'} {formatBlockRefCodes(refs)}
      </span>
    </div>
  );
}

function renderIndicator(
  task: TaskCardData,
  audience: 'internal' | 'external',
  onFlip: (e: React.MouseEvent) => void,
  internalChipHidden: boolean
) {
  if (audience === 'external') return null;

  // MKS-534 — nobody external on this project: the chip could only ever read
  // "Internal", so it carries no information. An EXTERNAL ticket keeps its
  // chip (an amber one is news on such a project, and on a flippable card it
  // is also the way back); so does every card while the roster is unknown.
  if (internalChipHidden && !task.isExternal) return null;

  const chipColor = task.isExternal ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700';
  const dotColor = task.isExternal ? 'bg-amber-500' : 'bg-green-500';
  const label = task.isExternal ? 'External' : 'Internal';

  if (task.canFlipVisibility) {
    return (
      <button
        type="button"
        onClick={onFlip}
        title={task.isExternal ? 'Mark as internal' : 'Mark as external'}
        // Phase 41 Plan 41-17 — hover:ring-gray-200 → hover:ring-ms-border;
        // amber-/green- chip palette retained per D-Tokens decision (Tailwind
        // semantic colors stay untouched and already align with v7 OK/At-risk
        // intent).
        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium shrink-0 hover:ring-2 hover:ring-ms-border ${chipColor}`}
      >
        <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} aria-hidden />
        {label}
      </button>
    );
  }

  // Static read-only chip.
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium shrink-0 ${chipColor}`}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} aria-hidden />
      {label}
    </span>
  );
}

/**
 * Is `el` entirely inside every scrolling ancestor and the viewport? A card in a
 * scrolled column can sit inside the window yet be clipped by its column.
 */
function isFullyVisible(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  if (
    rect.top < 0 ||
    rect.left < 0 ||
    rect.bottom > window.innerHeight ||
    rect.right > window.innerWidth
  ) {
    return false;
  }
  for (let parent = el.parentElement; parent; parent = parent.parentElement) {
    const { overflowX, overflowY } = window.getComputedStyle(parent);
    if (!/(auto|scroll|hidden)/.test(overflowX + overflowY)) continue;
    const box = parent.getBoundingClientRect();
    if (
      rect.top < box.top ||
      rect.bottom > box.bottom ||
      rect.left < box.left ||
      rect.right > box.right
    ) {
      return false;
    }
  }
  return true;
}
